// NPM Imports
import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Toastr } from "@revvsales/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router-dom";

// CSS Imports
import signIn from "./signin.module.scss";

// Internal Imports
import { GlobalUtils } from "../../utils";

// Service Imports
import {
  initiateResendVerificationService,
  initiateResetPasswordService,
  popDemoDocument,
  signInUserService,
  slackService,
} from "../../AuthService/authService";
import { redirectPostAuthentication } from "../../AuthService/authMethods";

// Destructuring
const { removeCookie, setUserCookies, writeDataToLocalStorage } = GlobalUtils;

function SignInForm(props) {
  const history = useHistory();

  const {
    data: { email, isVerified: isEmailVerified, selectedDomain },
  } = props;

  // Local States
  const [state, setState] = useState({
    errMsg: "",
    password: "",
    showEmailVerifyLoader: false,
    showForgotPswdLoader: false,
    showLoginLoader: false,
  });

  // Local Functions

  const _signInUser = () => {
    setState(prev => ({ ...prev, errMsg: "" }));

    if (!state.password) {
      setState(prev => ({ ...prev, errMsg: "Password can't be empty!" }));
    } else {
      setState(prev => ({ ...prev, showLoginLoader: true }));

      const payload = {
        user_email: email.toLocaleLowerCase(),
        password: state.password,
        org_domain: selectedDomain,
      };

      signInUserService(payload)
        .then(async res => {
          if (res && res.subdomain) {
            setUserCookies(res);
            // removing cookies after sign up
            removeCookie("SubCategoryName");
            removeCookie("CategoryName");
            removeCookie("TemplateName");
            removeCookie("InitiatedFrom");

            // Pop the document if user tried in the document playground
            const slackAuthentication = slackService(res);
            const retainDemoDocument = popDemoDocument();
            await Promise.all([slackAuthentication, retainDemoDocument])
              .then(response => {
                const docData = response[1];
                redirectPostAuthentication(res, docData, {
                  flow: "signin",
                  params: { method: "email" },
                });
              })
              .catch(() => {
                redirectPostAuthentication(res, null, {
                  flow: "signin",
                  params: { method: "email" },
                });
              });
          } else {
            setState(prev => ({
              ...prev,
              showLoginLoader: false,
              errMsg: "Something went wrong. Please try again!",
            }));
          }
        })
        .catch(err => {
          console.error(`SignIn => SignInForm => _signInUser => ${err}`);
          if (
            err.response &&
            (err.response.status === 401 || err.response.status === 400)
          ) {
            setState(prev => ({
              ...prev,
              showLoginLoader: false,
              errMsg: "Invalid Signin Details",
            }));
          } else {
            setState(prev => ({
              ...prev,
              showLoginLoader: false,
              errMsg: "Something went wrong. Please try again!",
            }));
          }
        });
    }
  };

  const _resendVerifyMail = () => {
    setState(prev => ({ ...prev, showEmailVerifyLoader: true }));
    initiateResendVerificationService(email, selectedDomain)
      .then(() => {
        Toastr.showToastr({
          type: "success",
          message: "Email sent successfully",
        });
        setState(prev => ({ ...prev, showEmailVerifyLoader: false }));
      })
      .catch(err => {
        if (err.response && err.response.status === 400) {
          setState(prev => ({
            ...prev,
            showEmailVerifyLoader: false,
            errMsg: "Invalid Details",
          }));
        } else {
          setState(prev => ({
            ...prev,
            showEmailVerifyLoader: false,
            errMsg: "Something went wrong. Please try again!",
          }));
        }
      });
  };

  const _onResetPswdClick = () => {
    setState(prev => ({ ...prev, showForgotPswdLoader: true }));
    const userObj = {
      user_email: email,
    };
    initiateResetPasswordService(userObj, selectedDomain)
      .then(() => {
        const obj = {
          email,
          subDomainName: selectedDomain,
        };
        Toastr.showToastr({
          type: "success",
          message: "Please check your mail for the secret code",
        });
        writeDataToLocalStorage("resetPasswordKeys", obj);
        setTimeout(() => history.replace("/reset-password/"), 0);
      })
      .catch(err => {
        if (err.response && err.response.status === 400) {
          setState(prev => ({
            ...prev,
            showForgotPswdLoader: false,
            errMsg: "Invalid Details",
          }));
        } else {
          setState(prev => ({
            ...prev,
            showForgotPswdLoader: false,
            errMsg: "Something went wrong. Please try again!",
          }));
        }
      });
  };

  const _onPasswordInputChange = e => {
    let trimedValue = e.target.value;
    if (trimedValue) {
      trimedValue = trimedValue.trim();
    }
    setState(prev => ({ ...prev, password: trimedValue, errMsg: "" }));
  };

  const _onPasswordInputKeyDown = e => {
    if (e.key === "Enter") {
      _signInUser();
    }
  };

  return (
    <Fragment>
      {/** HEADER SECTION */}
      <section>
        {state.errMsg && (
          <div className={signIn.errorMessage}>
            <span className={signIn.errorMessageIcon}>
              <FontAwesomeIcon icon={["far", "times-circle"]} />
            </span>
            {state.errMsg}
          </div>
        )}
      </section>

      {/** FORM SECTION */}
      <section>
        <div className={signIn.formSection}>
          <span className={signIn.fieldLabel}>Work Email</span>
          <Input
            data-test="signin-work-email"
            className={signIn.formControl}
            id="signin-email-field"
            type="email"
            placeholder="Enter your work email"
            name="email"
            value={email}
            disabled
          />
        </div>
        {isEmailVerified ? (
          /** SHOW PASSWORD FORM IF ENTERED EMAIL IS VERIFIED */
          <Fragment>
            <div className={signIn.formSection}>
              <span className={signIn.fieldLabel}>Password</span>
              <Input
                data-test="signin-password"
                className={signIn.formControl}
                id="signin-password-field"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={state.password}
                autoFocus
                onChange={e => _onPasswordInputChange(e)}
                onKeyDown={e => _onPasswordInputKeyDown(e)}
              />
              <div
                className={signIn.forgotPassword}
                data-test="signin-forgot-password">
                <span
                  className="fontcolor"
                  id="forgot-password-span"
                  role="button"
                  onKeyPress={() => {}}
                  tabIndex="0"
                  onClick={() => _onResetPswdClick()}>
                  Forgot my password
                </span>{" "}
                {state.showForgotPswdLoader && (
                  <FontAwesomeIcon
                    icon={["far", "spinner"]}
                    className="fa-spin"
                  />
                )}
              </div>
            </div>
            <div className={signIn.btnWrapper}>
              <Button
                type="four"
                data-test="signin-log-in"
                className={signIn.authBtn}
                id="signin-button-field"
                isLoading={state.showLoginLoader}
                onClick={() => _signInUser()}>
                Log in
              </Button>
            </div>
          </Fragment>
        ) : (
          /** SHOW RESEND VEFIRICATION SCREEN WHEN EMAIL IS NOT VERIFIED */
          <Fragment>
            <div className={signIn.verifyMsg}>
              <div className={signIn.vmHeader}>
                <FontAwesomeIcon
                  icon={["fal", "exclamation-triangle"]}
                  className={signIn.faTriangle}
                />
                Please verify your email to securely sign in to your Revv
                account.
              </div>
              <div className={signIn.vmContent}>
                A verification link has been sent to your email.{" "}
                <span
                  data-test="signin-resend-btn"
                  role="button"
                  tabIndex="0"
                  className={signIn.resendLink}
                  onClick={() => _resendVerifyMail()}
                  onKeyPress={() => {}}>
                  Click here to re-send
                </span>{" "}
                {state.showEmailVerifyLoader && (
                  <FontAwesomeIcon
                    icon={["far", "spinner"]}
                    className="fa-spin"
                  />
                )}
                <div>
                  Don&#39;t forget to check your spam folder just in case it
                  lands there.
                </div>
              </div>
            </div>
          </Fragment>
        )}
      </section>
    </Fragment>
  );
}

SignInForm.defaultProps = {};

SignInForm.propTypes = {
  data: PropTypes.shape({
    domains: PropTypes.arrayOf(
      PropTypes.shape({
        sub_domain: PropTypes.string.isRequired,
      }),
    ).isRequired,
    email: PropTypes.string.isRequired,
    isVerified: PropTypes.bool.isRequired,
    ociReSignUp: PropTypes.bool.isRequired,
    selectedDomain: PropTypes.string.isRequired,
  }).isRequired,
};

export default SignInForm;
